import axios, { AxiosInstance } from 'axios';

import { getXApiKeyHeader } from '../utils/request-headers/get-x-api-key-header';
import { getXAppVersionHeader } from '../utils/request-headers/get-x-app-version-header';
import { isUnhandledError } from '../utils/response/is-unhandled-error';
import { scheduleAppUpdate } from '../utils/app-update/schedule-app-update';
import { getCmsConfig, isCmsUrl } from './cms/cms.utils';
import { isEcpLocatorUrl } from './ecp-locator/ecp-locator.utils';
import { getAuthorizationHeader } from '../utils/request-headers/get-authorization-header';
import { isTokenExpired } from '../utils/response/is-token-expired';
import { CaptchaType } from '../graphql/configuration';
import { BASE_REST_URL } from '../constants/endpoints';
import { isTokenInvalid } from '../utils/response/is-token-invalid';
import { logoutActions } from '../graphql/user/actions/logout';
import { getTokenValue } from '../utils/user';

const callbacks: { [key: string]: () => void | Promise<string> } = {};

export const setCallback = (eventName: string, callback: () => void) => {
  callbacks[eventName] = callback;
};

const baseHeaders = {
  'x-api-key': getXApiKeyHeader(),
  'X-APP-Version': getXAppVersionHeader(),
};

const cmsApiGatewayHeaders = {
  'x-api-key': process.env.REACT_APP_API_GATEWAY_CMS_AK as string,
  'X-APP-Version': getXAppVersionHeader(),
};

export const restClient = axios.create({
  baseURL: BASE_REST_URL,
  headers: baseHeaders,
});

const getCaptchaHeaders = (token: string, type?: CaptchaType) => {
  switch (type) {
    case CaptchaType.smartCaptcha:
      return { ...baseHeaders, 'x-smartcaptcha-token': token };
    case CaptchaType.reCaptcha:
      return { ...baseHeaders, 'x-recaptcha-token': token };
    default:
      return { ...baseHeaders, 'x-smartcaptcha-token': token };
  }
};

export const restClientCaptcha = (token: string, captchaType?: CaptchaType): AxiosInstance => {
  return axios.create({
    baseURL: BASE_REST_URL,
    headers: getCaptchaHeaders(token, captchaType),
  });
};

restClient.interceptors.request.use((config) => {
  if (isCmsUrl(config.url)) {
    if (process.env.REACT_APP_IS_API_GATEWAY_ENABLED === 'true') {
      config.headers = { ...config.headers, ...cmsApiGatewayHeaders };
    }
    return getCmsConfig(config);
  }
  if (process.env.REACT_APP_IS_API_GATEWAY_ENABLED === 'true' && isEcpLocatorUrl(config.url)) {
    config.baseURL = process.env.REACT_APP_API_GATEWAY_LOCATOR_URL;
  }
  return config;
}, (error) => Promise.reject(error));

restClient.interceptors.response.use((response) => response, (error) => {
  if (error.response) {
    const { status, data } = error.response;
    switch (true) {
      case status === 401:
        if (isTokenInvalid(data) && getTokenValue()) {
          logoutActions();
        }

        if (callbacks.onTokenExpired && isTokenExpired(data)) {
          return (callbacks.onTokenExpired() as Promise<string>)
            .then((token) => {
              error.config.headers.Authorization = getAuthorizationHeader(token);
              return axios.request(error.config);
            });
        }
        break;
      case status === 426:
        scheduleAppUpdate();
        break;
      case status === 403:
        break;
      // reset-password component errors
      case status === 400:
        break;
      case isUnhandledError(status, error.request):
        if (callbacks.onUnknownErrorReceived) {
          callbacks.onUnknownErrorReceived();
        }
        break;
      default:
        break;
    }
  } else if (callbacks.onUnknownErrorReceived) {
    callbacks.onUnknownErrorReceived();
  }

  return Promise.reject(error);
});
